import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Check, Circle, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export default function CalendarPage() {
  const [date, setDate] = useState(new Date());
  const [reminders, setReminders] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", experiment_id: "" });

  const load = async () => {
    const [{ data: rem }, { data: exps }] = await Promise.all([
      api.get("/reminders"),
      api.get("/experiments"),
    ]);
    setReminders(rem);
    setExperiments(exps);
  };
  useEffect(() => { load(); }, []);

  const addReminder = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Title and date required");
    await api.post("/reminders", {
      title: form.title,
      date: new Date(form.date).toISOString(),
      experiment_id: form.experiment_id || null,
    });
    toast.success("Reminder added");
    setOpen(false);
    setForm({ title: "", date: "", experiment_id: "" });
    load();
  };

  const toggle = async (r) => {
    await api.put(`/reminders/${r.id}`, { done: !r.done });
    load();
  };

  const del = async (id) => {
    await api.delete(`/reminders/${id}`);
    toast.success("Deleted");
    load();
  };

  const reminderDates = reminders.map((r) => new Date(r.date));
  const dayReminders = reminders.filter((r) => {
    const d = new Date(r.date);
    return d.toDateString() === date.toDateString();
  });

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-6 animate-fade-in" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Schedule</div>
          <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight mt-1">Calendar & reminders</h1>
          <p className="text-sm font-body text-zinc-500 mt-1">Track incubation times, follow-ups, and deadlines.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-reminder-btn"><Plus className="h-4 w-4 mr-2" />New reminder</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New reminder</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Follow up with supervisor" data-testid="reminder-title-input" />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Date & time</Label>
                <Input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="reminder-date-input" />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Link to experiment (optional)</Label>
                <Select value={form.experiment_id || "none"} onValueChange={(v) => setForm({ ...form, experiment_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {experiments.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={addReminder} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-reminder-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-4">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            modifiers={{ hasReminder: reminderDates }}
            modifiersClassNames={{ hasReminder: "font-bold text-blue-600 relative" }}
          />
          <div className="text-xs font-mono text-zinc-500 mt-4">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-600 mr-2" /> days with reminders
          </div>
        </div>
        <div className="lg:col-span-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-blue-600" />
              <h2 className="font-sans font-semibold">{date.toDateString()}</h2>
            </div>
          </div>
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 max-h-[500px] overflow-y-auto">
            {dayReminders.length === 0 && (
              <li className="p-6 text-center text-sm font-mono text-zinc-500">no reminders on this day</li>
            )}
            {dayReminders.map((r) => (
              <li key={r.id} className="p-4 flex items-start gap-3" data-testid={`reminder-${r.id}`}>
                <button onClick={() => toggle(r)} className="mt-0.5">
                  {r.done ? <Check className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-zinc-400" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`font-body text-sm ${r.done ? "line-through text-zinc-400" : ""}`}>{r.title}</div>
                  <div className="text-xs font-mono text-zinc-500">{new Date(r.date).toLocaleTimeString()}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => del(r.id)} className="hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
        <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">All reminders</h2>
        </div>
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 max-h-[400px] overflow-y-auto">
          {reminders.length === 0 && <li className="p-6 text-center text-sm font-mono text-zinc-500">no reminders yet</li>}
          {reminders.map((r) => (
            <li key={r.id} className="p-4 flex items-center gap-3">
              <button onClick={() => toggle(r)}>
                {r.done ? <Check className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-zinc-400" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-body text-sm ${r.done ? "line-through text-zinc-400" : ""}`}>{r.title}</div>
                <div className="text-xs font-mono text-zinc-500">{new Date(r.date).toLocaleString()}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(r.id)} className="hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
